/**
 * AcceptInvitePage — handles workspace invitation acceptance.
 *
 * Route: /auth/accept-invite?token={token}
 *
 * Flow:
 * 1. Load invitation info from token (public — no auth required)
 * 2. If not logged in → prompt to sign in / sign up (with redirect back)
 * 3. If logged in → show invite details and accept button
 * 4. On accept → navigate to /overview with success toast
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SplitAuthLayout } from '@/layouts/SplitAuthLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { getInvitationInfo, acceptInvitation, fetchWorkspaces } from '@/services/authApi';
import type { InvitationInfo } from '@/types/auth';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

const ROLE_BADGE_CLASSES: Record<string, string> = {
  owner: 'bg-coral/10 text-coral-dark border-coral/20',
  admin: 'bg-teal-light text-teal-dark border-teal/20',
  editor: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  viewer: 'bg-muted text-muted-foreground border-border',
};

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { user, isLoading: authLoading } = useAuth();
  const { setWorkspaces, setCurrentWorkspace } = useWorkspaceStore();

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  // Load invitation info (public — no auth needed)
  useEffect(() => {
    if (!token) {
      setLoadError('No invitation token found in the URL.');
      return;
    }
    getInvitationInfo(token)
      .then(setInfo)
      .catch(() => setLoadError('This invitation link is invalid or has expired.'));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const result = await acceptInvitation(token);
      // Refresh workspace list so the new workspace appears
      const workspaces = await fetchWorkspaces();
      setWorkspaces(workspaces);
      const joined = workspaces.find((w) => w.id === result.workspace_id);
      if (joined) setCurrentWorkspace(joined);
      toast.success(`Welcome to ${result.workspace_name}!`);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to accept invitation';
      toast.error(msg);
    } finally {
      setAccepting(false);
    }
  };

  // Loading state
  if (authLoading || (!info && !loadError)) {
    return (
      <SplitAuthLayout>
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-coral border-t-transparent" />
        </div>
      </SplitAuthLayout>
    );
  }

  // Error state — invalid / expired token
  if (loadError || !info) {
    return (
      <SplitAuthLayout>
        <div className="space-y-6 text-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Invitation not found</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {loadError ?? 'This invitation link is invalid or has already been used.'}
            </p>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Go to login</Link>
          </Button>
        </div>
      </SplitAuthLayout>
    );
  }

  // Already used / expired / revoked
  if (!info.is_valid) {
    return (
      <SplitAuthLayout>
        <div className="space-y-6 text-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Invitation unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {info.status === 'accepted'
                ? 'This invitation has already been accepted.'
                : info.status === 'revoked'
                ? 'This invitation has been revoked.'
                : 'This invitation has expired.'}
            </p>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Go to login</Link>
          </Button>
        </div>
      </SplitAuthLayout>
    );
  }

  // Valid invitation — user not authenticated
  if (!user) {
    const redirectUrl = `/auth/accept-invite?token=${token}`;
    return (
      <SplitAuthLayout>
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-semibold tracking-tight">You've been invited</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>{info.invited_by_name}</strong> invited you to join{' '}
              <strong>{info.workspace_name}</strong> as{' '}
              <Badge
                variant="outline"
                className={`inline-flex ${ROLE_BADGE_CLASSES[info.role] ?? ''}`}
              >
                {ROLE_LABELS[info.role] ?? info.role}
              </Badge>
            </p>
          </div>
          <div className="space-y-3">
            <Button asChild className="w-full bg-coral hover:bg-coral-hover">
              <Link to={`/login?redirect=${encodeURIComponent(redirectUrl)}`}>
                Sign in to accept
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to={`/signup?redirect=${encodeURIComponent(redirectUrl)}`}>
                Create an account
              </Link>
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Invitation sent to{' '}
            <span className="font-medium text-foreground">{info.email}</span>
          </p>
        </div>
      </SplitAuthLayout>
    );
  }

  // Valid invitation — user authenticated
  return (
    <SplitAuthLayout>
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Join {info.workspace_name}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            <strong>{info.invited_by_name}</strong> has invited you to join as{' '}
            <Badge
              variant="outline"
              className={`inline-flex ${ROLE_BADGE_CLASSES[info.role] ?? ''}`}
            >
              {ROLE_LABELS[info.role] ?? info.role}
            </Badge>
          </p>
        </div>

        <div className="rounded-lg border bg-warm-bg p-4 text-sm text-muted-foreground">
          You're signed in as{' '}
          <span className="font-medium text-foreground">{user.email}</span>
          {info.email.toLowerCase() !== user.email?.toLowerCase() && (
            <p className="mt-1 text-destructive">
              This invitation was sent to {info.email}. Please sign in with that email to accept.
            </p>
          )}
        </div>

        <Button
          onClick={handleAccept}
          disabled={accepting || info.email.toLowerCase() !== (user.email ?? '').toLowerCase()}
          className="w-full bg-coral hover:bg-coral-hover"
        >
          {accepting ? 'Accepting...' : `Accept and join ${info.workspace_name}`}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Not you?{' '}
          <Link to="/login" className="underline hover:text-foreground">
            Sign in with a different account
          </Link>
        </p>
      </div>
    </SplitAuthLayout>
  );
}
